import { useEffect, useMemo, useRef, useState } from 'react';
import Button from './common/button.tsx';
import CrossIcon from '../assets/icons/cross.svg?react';
import { debounce } from 'lodash';

interface IAdvanceOption {
  key: string;
  title: string;
}
interface ISearchOptions {
  [key: string]: IAdvanceOption[] | string[];
}
interface IProps {
  onChangeFilter: (value?: string, type?: string) => void;
  searchBy: (IAdvanceOption | string)[];
  optionsData?: ISearchOptions;
  removeXicon?: boolean;
}

const Search = ({
  onChangeFilter,
  searchBy,
  optionsData,
  removeXicon,
}: IProps) => {
  const [input, setInput] = useState<string | undefined>();
  const isFirstRenderRef = useRef(true);
  const onChangeFilterRef = useRef(onChangeFilter);

  const defaultSearchKey =
    typeof searchBy[0] === 'string' ? searchBy[0] : searchBy[0].key;
  const [filterBy, setFilterBy] = useState<string>(defaultSearchKey);

  useEffect(() => {
    onChangeFilterRef.current = onChangeFilter;
  }, [onChangeFilter]);

  const updatePayloadLodash = useMemo(
    () =>
      debounce(({ input, filterBy }: { input?: string; filterBy: string }) => {
        onChangeFilterRef.current(input, filterBy);
      }, 300),
    [],
  );

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    updatePayloadLodash({ input, filterBy });

    return () => {
      updatePayloadLodash.cancel();
    };
  }, [filterBy, input, updatePayloadLodash]);

  const onClearFields = () => {
    updatePayloadLodash.cancel();
    setFilterBy(defaultSearchKey);
    setInput(undefined);
    onChangeFilterRef.current(undefined, defaultSearchKey);
  };

  const options = optionsData?.[filterBy] || undefined;

  return (
    <div className={'mt-2 flex h-10 flex-nowrap text-sm'}>
      <select
        onChange={(e) => {
          setInput(undefined);
          setFilterBy(e.target.value);
        }}
        value={filterBy}
        className={
          'h-full w-28 rounded-bl-lg rounded-tl-lg border border-disabled bg-light-gray'
        }
      >
        {searchBy.map((search) => {
          const isString = typeof search === 'string';
          const title = isString ? search : search.title;
          const key = isString ? search : search.key;

          return (
            <option key={key} value={key}>
              {title}
            </option>
          );
        })}
      </select>

      <div>
        {options ? (
          <select
            onChange={(e) => {
              setInput(e.target.value || undefined);
            }}
            value={input || ''}
            className={
              'h-full w-60 rounded-br-lg rounded-tr-lg border border-disabled'
            }
          >
            <option value={''} label={'Set value...'} />

            {options.map((item) => {
              const isString = typeof item === 'string';
              const value = isString ? item : item.key;
              const title = isString ? item : item.title;

              return (
                <option key={value} value={value}>
                  {title}
                </option>
              );
            })}
          </select>
        ) : (
          <input
            placeholder={'Search...'}
            value={input || ''}
            onChange={(e) => setInput(e.target.value)}
            className={
              'h-full w-60 rounded-br-lg rounded-tr-lg border-y border-r border-disabled pl-1'
            }
            type="text"
          />
        )}
      </div>

      {!removeXicon && (
        <Button
          className={'mx-1 bg-white p-2 hover:bg-light-blue'}
          onClick={onClearFields}
          icon={<CrossIcon />}
        />
      )}
    </div>
  );
};
export default Search;
